import { gql } from '@apollo/client';
import IconArrowDown from '@apollo/icons/default/IconArrowDown.svg';
import IconArrowUp from '@apollo/icons/default/IconArrowUp.svg';
import IconSingleService from '@apollo/icons/default/IconSingleService.svg';
import { IconButton } from '@apollo/orbit';
import classnames from 'classnames';
import _ from 'lodash';
import * as monacoEditor from 'monaco-editor';
import React, { useEffect, useMemo, useState } from 'react';

import { getEditorLineNumberFromParsedCoordinate } from 'src/app/graph/documentationPage/schemaPage/sdlEditorHelpers';
import { useInternalGraphLinking } from 'src/app/graph/hooks/useInternalGraphLinking';
import { FlatDiffItemFragment } from 'src/app/graph/proposalLayout/diffHelpers/diffHelpers';
import { getIdFriendlyCoordinateName } from 'src/app/graph/proposalLayout/proposalChanges/DetailDiff';
import { Loading } from 'src/components/common/loading/Loading';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { DiffViewType } from 'src/components/graphQLDiffView/diffViewHelpers';
import { GraphQLDiffView } from 'src/components/graphQLDiffView/GraphQLDiffView';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { tolerantParseSchema } from 'src/lib/chevrotain/tolerantParseSchema';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { ProposalChangeMismatchSeverity } from 'src/lib/graphqlTypes/types';
import { scrollToElementIfNotInBoundsOfParent } from 'src/lib/scrollTo';

import { useBaseAndProposedSchemas } from '../../useBaseAndProposedSchemas';
import {
  getFullyRemovedCoordinateNames,
  getOrderedLinkableGutterItems,
  isAddedOrChangedDiffItem,
  isRemovedDiffItem,
  parseCoordinateFromString,
} from '../proposalCheckHelpers';

import { ProposalCheckDiffItemButton } from './ProposalCheckDiffItemButtons';

export const ProposalCheckDetailsDiffDisplayFlatDiffItemFragment = gql`
  fragment ProposalCheckDetailsDiffDisplayFlatDiffItemFragment on ProposalsCheckTaskDiff {
    diffItem {
      ...FlatDiffItemFragment
    }
    hasApprovedProposal
    subgraph
  }
  ${FlatDiffItemFragment}
`;

export const ProposalCheckDetailsDiffDisplay = ({
  subgraphName,
  severityLevel,
  proposedSchemaHash,
  baseSchemaHash,
  diffItems,
  shouldScrollToFirstFailedCoordinateIfPossible,
  setHasScrolledToFirstFailedCoordinateOrLinked,
}: {
  subgraphName: string;
  severityLevel: ProposalChangeMismatchSeverity;
  proposedSchemaHash?: string;
  baseSchemaHash?: string;
  diffItems: GraphQLTypes.ProposalCheckDetailsDiffDisplayFlatDiffItemFragment[];
  shouldScrollToFirstFailedCoordinateIfPossible?: boolean;
  setHasScrolledToFirstFailedCoordinateOrLinked: React.Dispatch<
    React.SetStateAction<boolean>
  >;
}) => {
  const [shouldShowDiff, setShouldShowDiff] = useState(true);
  const { currentProposalCheckDetailsRoute } = useInternalGraphLinking();
  const {
    linkedSubgraphName,
    linkedCoordinateName,
    isDescription: isRouteDescription,
  } = useRouteParams(currentProposalCheckDetailsRoute);
  const [isMounted, setIsMounted] = useState(false);

  const {
    baseSchema,
    proposedSchema,
    loading: loadingDocumentData,
  } = useBaseAndProposedSchemas({
    baseSchemaHash,
    proposedSchemaHash,
  });

  const { modifiedTypeDefs, originalTypeDefs } = useMemo(
    () => ({
      modifiedTypeDefs: tolerantParseSchema(
        monacoEditor.editor.createModel(proposedSchema ?? ''),
      ).documentNode,
      originalTypeDefs: tolerantParseSchema(
        monacoEditor.editor.createModel(baseSchema ?? ''),
      ).documentNode,
    }),
    [baseSchema, proposedSchema],
  );

  const fullyRemovedCoordinateNames = useMemo(
    () => getFullyRemovedCoordinateNames(diffItems),
    [diffItems],
  );

  const removedCoordinatesToShowInGutter = useMemo(() => {
    const removedCoordinates = diffItems.filter((item) =>
      isRemovedDiffItem(item.diffItem, fullyRemovedCoordinateNames),
    );

    return getOrderedLinkableGutterItems(removedCoordinates, originalTypeDefs);
  }, [diffItems, fullyRemovedCoordinateNames, originalTypeDefs]);

  const addedOrChangedCoordinatesToShowInGutter = useMemo(() => {
    const addedOrChangedCoordinates = diffItems.filter((item) =>
      isAddedOrChangedDiffItem(item.diffItem, fullyRemovedCoordinateNames),
    );

    return getOrderedLinkableGutterItems(
      addedOrChangedCoordinates,
      modifiedTypeDefs,
    );
  }, [diffItems, fullyRemovedCoordinateNames, modifiedTypeDefs]);

  const gutterMarkers = useMemo(() => {
    const removedGutterMarkers = removedCoordinatesToShowInGutter.map(
      ([
        lineNumber,
        { hasApprovedProposal, linkableCoordinateName, isDescription },
      ]) => ({
        lineNumber,
        type: 'original' as const,
        marker: (
          <ProposalCheckDiffItemButton
            hasApprovedProposal={hasApprovedProposal}
            linkableCoordinateName={linkableCoordinateName}
            severityLevel={severityLevel}
            subgraphName={subgraphName}
            isDescription={isDescription}
          />
        ),
      }),
    );
    const addedAndChangedGutterMarkers =
      addedOrChangedCoordinatesToShowInGutter.map(
        ([
          lineNumber,
          { hasApprovedProposal, linkableCoordinateName, isDescription },
        ]) => ({
          lineNumber,
          type: 'modified' as const,
          marker: (
            <ProposalCheckDiffItemButton
              hasApprovedProposal={hasApprovedProposal}
              linkableCoordinateName={linkableCoordinateName}
              severityLevel={severityLevel}
              subgraphName={subgraphName}
              isDescription={isDescription}
            />
          ),
        }),
      );

    return [...removedGutterMarkers, ...addedAndChangedGutterMarkers];
  }, [
    addedOrChangedCoordinatesToShowInGutter,
    removedCoordinatesToShowInGutter,
    severityLevel,
    subgraphName,
  ]);

  let selectedLineNumber: number | undefined;
  let selectedViewType: DiffViewType | undefined;
  if (subgraphName === linkedSubgraphName && linkedCoordinateName) {
    const coordinate = parseCoordinateFromString(linkedCoordinateName);
    const modifiedLineNumber = getEditorLineNumberFromParsedCoordinate(
      modifiedTypeDefs,
      coordinate,
      isRouteDescription,
    );
    const originalLineNumber = getEditorLineNumberFromParsedCoordinate(
      originalTypeDefs,
      coordinate,
      isRouteDescription,
    );

    if (
      removedCoordinatesToShowInGutter.some(
        ([lineNumber]) => lineNumber === originalLineNumber,
      )
    ) {
      selectedLineNumber = originalLineNumber;
      selectedViewType = 'original';
    } else if (
      addedOrChangedCoordinatesToShowInGutter.some(
        ([lineNumber]) => lineNumber === modifiedLineNumber,
      )
    ) {
      selectedLineNumber = modifiedLineNumber;
      selectedViewType = 'modified';
    }
  }

  const firstVisibleFailedCoordinateAndLineToScrollTo = useMemo(() => {
    const firstFailedRemovedLineNumber = removedCoordinatesToShowInGutter.find(
      ([, { hasApprovedProposal }]) => !hasApprovedProposal,
    );
    const firstFailedAddedOrChangedLineNumber =
      addedOrChangedCoordinatesToShowInGutter.find(
        ([, { hasApprovedProposal }]) => !hasApprovedProposal,
      );

    return _.minBy(
      [firstFailedRemovedLineNumber, firstFailedAddedOrChangedLineNumber],
      (item) => item?.[0] ?? Infinity,
    );
  }, [
    addedOrChangedCoordinatesToShowInGutter,
    removedCoordinatesToShowInGutter,
  ]);

  useEffect(() => {
    if (subgraphName === linkedSubgraphName && linkedCoordinateName) {
      scrollToElementIfNotInBoundsOfParent(
        document.querySelector(
          `#${subgraphName} [id=${getIdFriendlyCoordinateName(
            linkedCoordinateName,
            isRouteDescription ? 'description' : 'none',
          )}]`,
        ),
        document.querySelector(`#${subgraphName} div:nth-child(2)`),
        {
          behavior: 'smooth',
          block: 'center',
        },
      );
      setHasScrolledToFirstFailedCoordinateOrLinked(true);
    } else if (
      shouldScrollToFirstFailedCoordinateIfPossible &&
      firstVisibleFailedCoordinateAndLineToScrollTo
    ) {
      scrollToElementIfNotInBoundsOfParent(
        document.querySelector(
          `#${subgraphName} [id=${getIdFriendlyCoordinateName(firstVisibleFailedCoordinateAndLineToScrollTo[1].linkableCoordinateName, firstVisibleFailedCoordinateAndLineToScrollTo[1].isDescription ? 'description' : 'none')}]`,
        ),
        document.querySelector(`#${subgraphName} div:nth-child(2)`),
        {
          behavior: 'smooth',
          block: 'center',
        },
      );
      setHasScrolledToFirstFailedCoordinateOrLinked(true);
    }
  }, [
    linkedCoordinateName,
    loadingDocumentData,
    linkedSubgraphName,
    subgraphName,
    gutterMarkers,
    isMounted,
    isRouteDescription,
    firstVisibleFailedCoordinateAndLineToScrollTo,
    shouldScrollToFirstFailedCoordinateIfPossible,
    setHasScrolledToFirstFailedCoordinateOrLinked,
  ]);

  return (
    <div
      id={subgraphName}
      className="flex h-auto min-h-120 w-full flex-col rounded-lg border border-primary"
    >
      <div
        className={classnames(
          'flex items-center justify-between p-4',
          shouldShowDiff && 'border-b border-primary',
        )}
      >
        <div className="flex items-center">
          <IconSingleService className="text-icon-primary" />
          <span className="ml-2 font-semibold">{subgraphName}</span>
        </div>
        <Tooltip
          label={shouldShowDiff ? 'Collapse changes' : 'Show changes'}
          placement="top"
        >
          <IconButton
            size="sm"
            variant="secondary"
            aria-label={shouldShowDiff ? 'Collapse changes' : 'Show changes'}
            onClick={() => setShouldShowDiff((value) => !value)}
          >
            {shouldShowDiff ? <IconArrowUp /> : <IconArrowDown />}
          </IconButton>
        </Tooltip>
      </div>
      {loadingDocumentData ? (
        <div className="h-120">
          <Loading />
        </div>
      ) : (
        shouldShowDiff && (
          <GraphQLDiffView
            oldSdl={baseSchema}
            newSdl={proposedSchema}
            className="h-120"
            lineVisibilityType="only-changes"
            lineNumberClassName="ml-3"
            gutterMarkers={gutterMarkers}
            onMount={() => setIsMounted(true)}
            lineProps={({ lineNumber, type }) => {
              const changedCoordinateIfItExists =
                type === 'original'
                  ? removedCoordinatesToShowInGutter.find(
                      ([coordinateLineNumber]) =>
                        coordinateLineNumber === lineNumber,
                    )
                  : addedOrChangedCoordinatesToShowInGutter.find(
                      ([coordinateLineNumber]) =>
                        coordinateLineNumber === lineNumber,
                    );

              const isSelectedCoordinate =
                lineNumber === selectedLineNumber && type === selectedViewType;
              const isDescription =
                changedCoordinateIfItExists?.[1].isDescription;

              return {
                ...(changedCoordinateIfItExists
                  ? {
                      id: getIdFriendlyCoordinateName(
                        changedCoordinateIfItExists[1].linkableCoordinateName,
                        isDescription ? 'description' : 'none',
                      ),
                    }
                  : {}),
                ...(isSelectedCoordinate
                  ? {
                      className:
                        'bg-highlighted border-y border-dashed border-highlighted',
                    }
                  : { className: 'border-y border-transparent' }),
              };
            }}
          />
        )
      )}
    </div>
  );
};
